/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiKeysAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import ConfirmDialog from '../common/ConfirmDialog'
import { useNameOf } from '../records/PersonInput'
import CopyField from './CopyField'

const apiBase = () => `${window.location.origin}/executions/api/v1/public/integrations`

const NewKeyDialog = ({ onClose }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [access, setAccess] = useState('READ_WRITE')
  const create = useMutation({ mutationFn: () => apiKeysAPI.create(name.trim(), access), onSuccess: () => queryClient.invalidateQueries({ queryKey: [tenantId, 'api-keys'] }) })
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{create.data ? 'Copy your key now' : 'New API key'}</DialogTitle>
      <DialogContent>
        {create.data ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">This is the only time the key is shown. Store it in the tool that uses it (Zapier, n8n…).</Alert>
            <CopyField label="API key" value={create.data.key} />
            <CopyField label="API address" value={apiBase()} helperText="Send the key in the X-Api-Key header. GET /me checks the connection." />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {create.error && <Alert severity="error">{getErrorMessage(create.error)}</Alert>}
            <TextField label="Name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Zapier" inputProps={{ maxLength: 100 }} />
            <TextField select label="Can" value={access} onChange={(e) => setAccess(e.target.value)}>
              <MenuItem value="READ_WRITE">Read, create and update records</MenuItem>
              <MenuItem value="READ">Only read records</MenuItem>
            </TextField>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {create.data ? <Button variant="contained" onClick={onClose}>Done</Button> : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Create key</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

const ApiKeysTab = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const nameOf = useNameOf()
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState(null)
  const { data: keys = [] } = useQuery({ queryKey: [tenantId, 'api-keys'], queryFn: apiKeysAPI.list })
  const revoke = useMutation({ mutationFn: apiKeysAPI.revoke, onSuccess: () => { setRevoking(null); queryClient.invalidateQueries({ queryKey: [tenantId, 'api-keys'] }) } })
  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Let other tools (Zapier, n8n, your own scripts) read and write records. Changes they make show on records as “API key: name”.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ flexShrink: 0, whiteSpace: 'nowrap', ml: 2 }} onClick={() => setCreating(true)}>New API key</Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Can</TableCell><TableCell>Last used</TableCell><TableCell>Made by</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {keys.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No API keys.</Typography></TableCell></TableRow>}
            {keys.map((k) => (
              <TableRow key={k.id}>
                <TableCell sx={{ fontWeight: 500 }}>{k.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>vk_{k.prefix}_…</Typography></TableCell>
                <TableCell>{k.access === 'READ' ? 'Read' : 'Read & write'}</TableCell>
                <TableCell>{k.lastUsedAt ? formatRelativeTime(k.lastUsedAt) : 'Never'}</TableCell>
                <TableCell>{nameOf(k.createdBy)}</TableCell>
                <TableCell align="right"><Tooltip title="Revoke"><IconButton onClick={() => setRevoking(k)} aria-label={`Revoke ${k.name}`}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {creating && <NewKeyDialog onClose={() => setCreating(false)} />}
      <ConfirmDialog open={!!revoking} title="Revoke API key?" message={`Anything using “${revoking?.name}” stops working right away.`}
        confirmLabel="Revoke" destructive busy={revoke.isPending} onConfirm={() => revoke.mutate(revoking.id)} onClose={() => setRevoking(null)} />
    </Box>
  )
}

export default ApiKeysTab
